import { Box, CircularProgress } from '@mui/material'
import LeftToolbarPanel from '../Toolbars/LeftToolbarPanel/LeftToolbarPanel'
import RightToolbarPanel from '../Toolbars/RightToolbarPanel/RightToolbarPanel'
import { useSelector } from 'react-redux'
import { RootState } from '../../store'
import ItemList from '../TakeOff/ItemList'
import { ConstructionTab } from '../../reducers/construction'
import ExpandableImage from '../ExpandableImage'
import TopPanel from '../Toolbars/TopPanel/TopPanel'
import BottomPanel from '../Toolbars/BottomToolbarPanel/BottomToolbarPanel'
import StartValidationModal from '../Validation/StartValidationModal'
import SpacialAI from '../Validation/SpacialAI'
import ObjectOptions from '../Toolbars/ObjectOptions/ObjectOptions'
import Draggable from 'react-draggable'
import { UpdateStructuralSettings } from '../UpdateStructuralSettings'
import Measurements from '../Toolbars/Measurements/Measurements'
import { BuildingEditingTool } from '../../models/Construction'

interface ViewerOverlayProps {
  imageHeight: number
  viewerSize: {
    width: number
    height: number
  }
  expandableImageSrc: string
  onSwitchViews: () => void
}

const ViewerOverlay = ({
  viewerSize,
  imageHeight,
  expandableImageSrc,
  onSwitchViews,
}: ViewerOverlayProps) => {
  const isItemListPresented = useSelector(
    (state: RootState) => state.takeOff.isItemListPresented,
  )
  const isTakeOffTab = useSelector(
    (state: RootState) =>
      state.construction.selectedConstructionTab === ConstructionTab.TakeOff,
  )

  const isValidationTab = useSelector(
    (state: RootState) =>
      state.construction.selectedConstructionTab === ConstructionTab.Validation,
  )

  const isValidationTaskExists = Boolean(
    useSelector((state: RootState) => state.validation.currentValidationTask),
  )

  const isCodeValidationStatusChecking = Boolean(
    useSelector(
      (state: RootState) => state.validation.isCodeValidationStatusChecking,
    ),
  )

  const isCodeValidationResultSelected = Boolean(
    useSelector(
      (state: RootState) => state.validation.selectedCodeValidationRule,
    ),
  )

  const isShowStructuralSettingModal = Boolean(
    useSelector(
      (state: RootState) => state.structural.showStructuralSettingsModal,
    ),
  )

  const isShowLoader = useSelector(
    (state: RootState) => state.structural.showLoader,
  )

  const isMeasurementsActive = useSelector(
    (state: RootState) =>
      state.construction.selectedTool === BuildingEditingTool.Measure,
  )

  return (
    <Box
      sx={{
        width: viewerSize.width,
        height: viewerSize.height,
        position: 'fixed',
        pointerEvents: 'none',
        display: 'grid',
        '& > *': {
          zIndex: 100,
          display: 'flex',
          pointerEvents: 'none',
          width: viewerSize.width,
          height: viewerSize.height,
          gridArea: '1 / 1 / 1 / 1',
        },
      }}
    >
      {isTakeOffTab && isItemListPresented && (
        <Box>
          <ItemList />
        </Box>
      )}

      <Box justifyContent='flex-end'>
        <RightToolbarPanel />
      </Box>

      <Box justifyContent='flex-start'>
        <LeftToolbarPanel />
      </Box>

      <Box justifyContent='center' alignItems='flex-start'>
        <TopPanel />
      </Box>

      <Box justifyContent='center' alignItems='flex-end'>
        <BottomPanel />
      </Box>
      {isValidationTab &&
      !isCodeValidationStatusChecking &&
      !isValidationTaskExists ? (
        <Box
          sx={{
            position: 'absolute',
            top: 0,
            left: 0,
            width: '100%',
            height: '100%',
            display: 'flex',
            justifyContent: 'center',
            alignItems: 'center',
            zIndex: 200,
            pointerEvents: 'auto',
          }}
        >
          <StartValidationModal />
        </Box>
      ) : null}

      {isCodeValidationResultSelected && (
        <Draggable handle='.spacial-ai-drag-handle'>
          <Box
            className='spacial-ai-drag-handle'
            sx={{
              position: 'absolute',
              bottom: 0,
              right: 0,
              width: '350px !important',
              height: '420px !important',
              maxHeight: '80vh',
              display: 'flex',
              flexDirection: 'column',
              borderRadius: '8px',
              boxShadow: 8,
              zIndex: 300,
              pointerEvents: 'auto',
              overflow: 'hidden',
            }}
          >
            <Box
              sx={{
                flexGrow: 1,
                padding: '6px 8px 8px',
                overflowY: 'auto',
                borderBottomLeftRadius: '0px',
                borderBottomRightRadius: '8px',
                height: '100%',
                fontSize: '13px',
                lineHeight: 1.4,
              }}
            >
              <div style={{ fontSize: '13px', lineHeight: 1.4 }}>
                <SpacialAI />
              </div>
            </Box>
          </Box>
        </Draggable>
      )}
      <UpdateStructuralSettings open={isShowStructuralSettingModal} />
      {isShowLoader ? (
        <Box
          sx={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            p: 0,
          }}
        >
          <CircularProgress />
        </Box>
      ) : (
        ''
      )}

      <ObjectOptions />

      {isMeasurementsActive && <Measurements />}
    </Box>
  )
}

export default ViewerOverlay
